<template>
	<div
		class="results-grid"
		:class="{ 'results-grid--loading': loading || loading_liked_options }"
	>
		<TransitionGroup
			name="grid"
			tag="ul"
			class="grid grid--xs flex flex--wrap relative mb-32"
		>
			<li
				v-for="option, index in options"
				:key="'database-option-' + option.id"
				class="grid__item a3-12 f6-6"
			>
				<div
					class="database-grid-card"
					@click="open_gallery( index )"
				>
					<img
						v-if="option.image_small_url || option.thumbnail_url"
						:src="option.image_small_url || option.thumbnail_url"
						:alt="option.title"
						class="database-grid-card__image"
					>
					<div
						v-else
						class="database-grid-card__image database-grid-card__image--default"
					/>
					<div class="database-grid-card__info">
						<h2 class="database-grid-card__title">
							{{ option.related_project[0].title }}
						</h2>
						<div class="flex flex--wrap flex--align-center flex--gap-large">
							<button
								class="database-grid-card__like-button"
								:class="[
									liked_options.includes( parseInt( option.id ) ) ? 'database-grid-card__like-button--liked' : 'database-grid-card__like-button--unliked',
									{ 'database-grid-card__like-button--loading': loading_likes.includes( option.id ) }
								]"
								:disabled="loading_likes.includes( option.id )"
								@click.stop="toggle_option_like( option.id )"
							>
								{{ option.likes ?? 0 }}
							</button>
							<p class="database-grid-card__date">
								{{ getDate( option.postDate ) }}
							</p>
						</div>
					</div>
				</div>
			</li>
		</TransitionGroup>
	</div>

	<Teleport to="#modal">
		<Transition name="fade">
			<DatabaseGalleryModalComponent
				v-if="gallery_options && gallery_options.length && show_gallery_modal"
				:options="gallery_options"
				:selected-gallery-index="selected_gallery_index"
				:liked-options="liked_options"
				:loading-modal-likes="loading_modal_likes"
				@select-prev="selectPrevGalleryOption"
				@select-next="selectNextGalleryOption"
				@close-modal="close_gallery_modal"
				@update-likes="toggle_option_like"
				@fetch-more-results="emit( 'fetch-more-results' )"
				@update-database-tags="updateDatabaseTags"
			/>
		</Transition>
	</Teleport>
</template>

<script setup>
import DatabaseGalleryModalComponent from '../components/modals/DatabaseGalleryModalComponent.vue';

import moment from 'moment';
import { useDataStore } from '../stores/data';
import { ref, computed, onMounted, defineEmits, defineProps } from 'vue';

const emit = defineEmits( ['fetch-more-results', 'update-database-likes', 'update-database-tags'] );

const props = defineProps( {
	options: {
		type: Array,
		required: true
	},
	loading: {
		type: Boolean,
		required: true
	},
} );

const data_store = useDataStore();

const {
	getDatabaseLikesOptionById,
	getLikedOptions,
	likeOption,
	unlikeOption,
} = data_store;

const loading_liked_options = ref( false );
const liked_options = ref( [] );
const loading_likes = ref( [] );
const loading_modal_likes = ref( false );

const fetch_likes = async() => {
	try {
		const response = await getLikedOptions();
		if ( response.data && response.data.likedOptions ) {
			liked_options.value = response.data.likedOptions;
		}
	} catch ( error ) {
		// console.log( error );
	}
};

const toggle_option_like = async( option_id ) => {
	loading_modal_likes.value = true;
	loading_likes.value.push( option_id );
	if ( liked_options.value.includes( parseInt( option_id ) ) ) {
		await unlikeOption( parseInt( option_id ) );
	} else {
		await likeOption( parseInt( option_id ) );
	}
	await fetch_likes();
	await update_likes( option_id );
	loading_likes.value.splice( loading_likes.value.indexOf( option_id ), 1 );
};

const update_likes = async( option_id ) => {
	try {
		const response = await getDatabaseLikesOptionById( option_id );
		if ( response.data.entry ) {
			emit( 'update-database-likes', option_id, response.data.entry.likes );
		}
		loading_modal_likes.value = false;
	} catch ( error ) {
		// console.log( error );
	}
};

const getDate = ( timestamp ) => {
	return moment( new Date( timestamp ) ).format( 'D/M/YY' );
};

// Gallery
const show_gallery_modal = ref( false );
const selected_gallery_index = ref( null );

const selectPrevGalleryOption = () => { // eslint-disable-line snakecasejs/snakecasejs
	selected_gallery_index.value = selected_gallery_index.value === 0 ? 0 : selected_gallery_index.value - 1;
};

const selectNextGalleryOption = () => { // eslint-disable-line snakecasejs/snakecasejs
	if ( gallery_options.value.length - 1 === selected_gallery_index.value ) {
		return;
	}
	selected_gallery_index.value = selected_gallery_index.value + 1;
};

const gallery_options = computed( () => {
	return props.options.map( option => {
		return {
			...option,
			title: `${option.related_project[0].title} - ${option.title}`,
			image_url: option.image_large_url,
		};
	} );
} );

const open_gallery = ( index ) => {
	selected_gallery_index.value = index;
	show_gallery_modal.value = true;
};

const close_gallery_modal = () => {
	show_gallery_modal.value = false;
	selected_gallery_index.value = null;
};

onMounted( async() => {
	loading_liked_options.value = true;
	await fetch_likes();
	loading_liked_options.value = false;
} );

function updateDatabaseTags( updated_database_options, select_option_id ) {
	emit( 'update-database-tags', updated_database_options, select_option_id );
}

</script>
